import type { LotSizingLine } from '@/types/core/lot-sizing/lot-sizing-line';

export type UpdateExistingPurchaseOrderLineInput =
  | {
      id: number;
      quantity: number;
    }
  | {
      id: number;
      delete: true;
    };

export type CreatePurchaseOrderLineInput = {
  productId: number;
  quantity: number;
};

export type UpdateLotSizingPurchaseOrderLineInput =
  | UpdateExistingPurchaseOrderLineInput
  | CreatePurchaseOrderLineInput;
export type LotSizingPurchaseOrderDraftChange = {
  id: number;
  supplierId: number;
  productId: number;
  unitPrice: number;
  unitVolume: number | null;
  originalQuantity: number;
  originalChecked: boolean;
  quantity: number;
  checked: boolean;
};

export type LotSizingPurchaseOrderDraftByLineId = Record<
  string,
  LotSizingPurchaseOrderDraftChange
>;

export type LotSizingPurchaseOrderDraftAddedLineById = Record<
  string,
  LotSizingLine
>;
export type LotSizingPurchaseOrderDraftPatch =
  | {
      quantity: number;
    }
  | {
      checked: boolean;
    };

function getDraftLineKey(lineId: number): string {
  return String(lineId);
}

function areChangesEqual(
  left: LotSizingPurchaseOrderDraftChange | undefined,
  right: LotSizingPurchaseOrderDraftChange,
): boolean {
  if (left === undefined) {
    return false;
  }

  return (
    left.originalQuantity === right.originalQuantity &&
    left.originalChecked === right.originalChecked &&
    left.quantity === right.quantity &&
    left.checked === right.checked
  );
}

export function buildDraftChange(
  line: LotSizingLine,
  existingChange: LotSizingPurchaseOrderDraftChange | undefined,
  patch: LotSizingPurchaseOrderDraftPatch,
): LotSizingPurchaseOrderDraftChange | null {
  const quantity =
    'quantity' in patch
      ? patch.quantity
      : (existingChange?.quantity ?? line.quantity);
  const checked =
    'checked' in patch
      ? patch.checked
      : (existingChange?.checked ?? line.checked);

  const nextChange: LotSizingPurchaseOrderDraftChange = {
    id: line.id,
    supplierId: line.supplierId,
    productId: line.product.id,
    unitPrice: line.unitPrice,
    unitVolume: line.unitVolume,
    originalQuantity: existingChange?.originalQuantity ?? line.quantity,
    originalChecked: existingChange?.originalChecked ?? line.checked,
    quantity,
    checked,
  };

  return nextChange.quantity === nextChange.originalQuantity &&
    nextChange.checked === nextChange.originalChecked
    ? null
    : nextChange;
}

export function setDraftChange(
  draftByLineId: LotSizingPurchaseOrderDraftByLineId,
  line: LotSizingLine,
  patch: LotSizingPurchaseOrderDraftPatch,
): LotSizingPurchaseOrderDraftByLineId {
  const key = getDraftLineKey(line.id);
  const nextChange = buildDraftChange(line, draftByLineId[key], patch);

  if (nextChange === null) {
    if (!(key in draftByLineId)) {
      return draftByLineId;
    }

    const nextDraftByLineId = { ...draftByLineId };
    delete nextDraftByLineId[key];
    return nextDraftByLineId;
  }

  if (areChangesEqual(draftByLineId[key], nextChange)) {
    return draftByLineId;
  }

  return {
    ...draftByLineId,
    [key]: nextChange,
  };
}

export function getDraftDisplayLine(
  line: LotSizingLine,
  draftByLineId: LotSizingPurchaseOrderDraftByLineId,
): LotSizingLine {
  const draftChange = draftByLineId[getDraftLineKey(line.id)];

  if (draftChange === undefined) {
    return line;
  }

  return {
    ...line,
    quantity: draftChange.quantity,
    checked: draftChange.checked,
  };
}

export function buildDraftSavePayload(
  draftByLineId: LotSizingPurchaseOrderDraftByLineId,
  draftAddedLinesById: LotSizingPurchaseOrderDraftAddedLineById,
): UpdateLotSizingPurchaseOrderLineInput[] {
  const updatedExistingLines = Object.values(draftByLineId).map(
    (draftChange) =>
      draftChange.checked
        ? {
            id: draftChange.id,
            quantity: draftChange.quantity,
          }
        : {
            id: draftChange.id,
            delete: true as const,
          },
  );
  const createdLines = Object.values(draftAddedLinesById)
    .filter((line) => line.checked)
    .map((line) => ({
      productId: line.product.id,
      quantity: line.quantity,
    }));

  return [...updatedExistingLines, ...createdLines];
}
